import React, { useState, useEffect } from 'react';
import TouchButton from '../ui/TouchButton';
import { TouchInput, TouchFormGroup } from '../ui/TouchForm';

const LocationTypeFilter = ({ 
  selectedTypes, 
  onTypesChange,
  selectedCategories,
  onCategoriesChange,
  selectedNeighborhoods,
  onNeighborhoodsChange 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [customNeighborhood, setCustomNeighborhood] = useState('');

  const locationTypes = [
    { id: 'restaurant', label: 'Restaurants', icon: '🍽️', category: 'dining' },
    { id: 'hotel', label: 'Hotels', icon: '🏨', category: 'accommodation' },
    { id: 'attraction', label: 'Attractions', icon: '🗼', category: 'entertainment' },
    { id: 'transport', label: 'Transportation', icon: '🚇', category: 'transportation' },
    { id: 'shopping', label: 'Shopping', icon: '🛍️', category: 'shopping' },
    { id: 'nightlife', label: 'Nightlife', icon: '🌙', category: 'entertainment' },
    { id: 'park', label: 'Parks', icon: '🌳', category: 'recreation' },
    { id: 'beach', label: 'Beaches', icon: '🏖️', category: 'recreation' },
    { id: 'museum', label: 'Museums', icon: '🏛️', category: 'culture' },
    { id: 'hospital', label: 'Hospitals', icon: '🏥', category: 'health' },
    { id: 'police', label: 'Police Stations', icon: '🚔', category: 'emergency' },
    { id: 'embassy', label: 'Embassies', icon: '🏛️', category: 'government' },
    { id: 'bank', label: 'Banks', icon: '🏦', category: 'finance' },
    { id: 'pharmacy', label: 'Pharmacies', icon: '💊', category: 'health' },
    { id: 'gas-station', label: 'Gas Stations', icon: '⛽', category: 'transportation' },
    { id: 'airport', label: 'Airports', icon: '✈️', category: 'transportation' },
    { id: 'bus-station', label: 'Bus Stations', icon: '🚌', category: 'transportation' },
    { id: 'train-station', label: 'Train Stations', icon: '🚂', category: 'transportation' },
    { id: 'subway', label: 'Subway Stations', icon: '🚇', category: 'transportation' },
    { id: 'university', label: 'Universities', icon: '🎓', category: 'education' },
    { id: 'school', label: 'Schools', icon: '🏫', category: 'education' },
    { id: 'library', label: 'Libraries', icon: '📚', category: 'culture' },
    { id: 'theater', label: 'Theaters', icon: '🎭', category: 'entertainment' },
    { id: 'stadium', label: 'Stadiums', icon: '🏟️', category: 'entertainment' }
  ];

  const categories = [
    { id: 'dining', label: 'Dining & Food', icon: '🍽️' },
    { id: 'accommodation', label: 'Hotels & Lodging', icon: '🏨' },
    { id: 'entertainment', label: 'Entertainment', icon: '🎭' },
    { id: 'transportation', label: 'Transportation', icon: '🚇' },
    { id: 'shopping', label: 'Shopping', icon: '🛍️' },
    { id: 'health', label: 'Health & Medical', icon: '🏥' },
    { id: 'education', label: 'Education', icon: '🎓' },
    { id: 'government', label: 'Government', icon: '🏛️' },
    { id: 'finance', label: 'Finance', icon: '🏦' },
    { id: 'recreation', label: 'Recreation', icon: '🎯' },
    { id: 'culture', label: 'Arts & Culture', icon: '🎨' },
    { id: 'emergency', label: 'Emergency Services', icon: '🚨' }
  ];

  const popularNeighborhoods = [
    'Manhattan', 'Brooklyn', 'Queens', 'Bronx', 'Staten Island',
    'Times Square', 'Central Park Area', 'SoHo', 'Greenwich Village',
    'Upper East Side', 'Lower East Side', 'Chinatown', 'Little Italy',
    'Financial District', 'Midtown', 'Upper West Side'
  ];

  // Filter location types based on search term
  const filteredTypes = locationTypes.filter(type =>
    type.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleType = (typeId) => {
    const newTypes = selectedTypes.includes(typeId)
      ? selectedTypes.filter(id => id !== typeId)
      : [...selectedTypes, typeId];
    onTypesChange(newTypes);
  };

  const toggleCategory = (categoryId) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    onCategoriesChange(newCategories);
  };

  const toggleNeighborhood = (neighborhood) => {
    const newNeighborhoods = selectedNeighborhoods.includes(neighborhood)
      ? selectedNeighborhoods.filter(n => n !== neighborhood)
      : [...selectedNeighborhoods, neighborhood];
    onNeighborhoodsChange(newNeighborhoods);
  };

  const addCustomNeighborhood = () => {
    if (customNeighborhood.trim() && !selectedNeighborhoods.includes(customNeighborhood.trim())) {
      onNeighborhoodsChange([...selectedNeighborhoods, customNeighborhood.trim()]);
      setCustomNeighborhood('');
    }
  };

  const selectCategoryTypes = (categoryId) => {
    const categoryTypes = locationTypes
      .filter(type => type.category === categoryId)
      .map(type => type.id);
    
    const allSelected = categoryTypes.every(typeId => selectedTypes.includes(typeId));
    
    if (allSelected) {
      // Deselect all types in this category
      const newTypes = selectedTypes.filter(typeId => !categoryTypes.includes(typeId));
      onTypesChange(newTypes);
    } else {
      // Select all types in this category
      const newTypes = [...new Set([...selectedTypes, ...categoryTypes])];
      onTypesChange(newTypes);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Categories Selection */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          🏷️ Location Categories:
        </label>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '8px' }}>
          {categories.map(category => {
            const categoryTypes = locationTypes
              .filter(type => type.category === category.id)
              .map(type => type.id);
            const selectedCount = categoryTypes.filter(typeId => selectedTypes.includes(typeId)).length;
            const isSelected = selectedCategories.includes(category.id);
            
            return (
              <div key={category.id} style={{ position: 'relative' }}>
                <TouchButton
                  onClick={() => toggleCategory(category.id)}
                  variant={isSelected ? 'primary' : 'outline'}
                  size="small"
                  style={{ 
                    width: '100%',
                    fontSize: '12px',
                    justifyContent: 'flex-start',
                    gap: '8px',
                    position: 'relative'
                  }}
                >
                  <span>{category.icon}</span>
                  <span>{category.label}</span>
                  {selectedCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      backgroundColor: '#2196F3',
                      color: 'white',
                      borderRadius: '50%',
                      width: '18px',
                      height: '18px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {selectedCount}
                    </span>
                  )}
                </TouchButton>
              </div>
            );
          })}
        </div>
      </TouchFormGroup>

      {/* Location Types Selection */}
      <TouchFormGroup>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <label style={{ fontSize: '14px', fontWeight: '600' }}>
            📍 Specific Location Types:
          </label>
          <div style={{ display: 'flex', gap: '6px' }}>
            <TouchButton
              onClick={() => onTypesChange(locationTypes.map(t => t.id))}
              variant="ghost"
              size="small"
              style={{ fontSize: '11px' }}
            >
              Select All
            </TouchButton>
            <TouchButton
              onClick={() => onTypesChange([])}
              variant="ghost"
              size="small"
              style={{ fontSize: '11px' }}
            >
              Clear
            </TouchButton>
          </div>
        </div>
        
        {/* Search Box */}
        <TouchInput
          placeholder="Search location types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: '12px' }}
        />
        
        {/* Location Types Grid */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
          gap: '8px',
          maxHeight: '300px',
          overflowY: 'auto',
          padding: '4px'
        }}>
          {filteredTypes.map(type => (
            <TouchButton
              key={type.id}
              onClick={() => toggleType(type.id)}
              variant={selectedTypes.includes(type.id) ? 'primary' : 'outline'}
              size="small"
              style={{ 
                fontSize: '12px',
                justifyContent: 'flex-start',
                gap: '8px'
              }}
            >
              <span>{type.icon}</span>
              <span>{type.label}</span>
            </TouchButton>
          ))}
        </div>
        
        {filteredTypes.length === 0 && searchTerm && (
          <div style={{ 
            textAlign: 'center', 
            color: '#666', 
            fontSize: '14px',
            padding: '20px'
          }}>
            No location types found for "{searchTerm}"
          </div>
        )}
      </TouchFormGroup>

      {/* Neighborhoods */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          🏘️ Neighborhoods:
        </label>
        
        {/* Popular Neighborhoods */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Popular Areas:
          </div>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '6px'
          }}>
            {popularNeighborhoods.slice(0, 8).map(neighborhood => (
              <TouchButton
                key={neighborhood}
                onClick={() => toggleNeighborhood(neighborhood)}
                variant={selectedNeighborhoods.includes(neighborhood) ? 'primary' : 'outline'}
                size="small"
                style={{ fontSize: '11px' }}
              >
                {neighborhood}
              </TouchButton>
            ))}
          </div>
        </div>
        
        {/* Custom Neighborhood Input */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <TouchInput
            placeholder="Add custom neighborhood..."
            value={customNeighborhood}
            onChange={(e) => setCustomNeighborhood(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomNeighborhood();
              }
            }}
            style={{ flex: 1 }}
          />
          <TouchButton
            onClick={addCustomNeighborhood}
            disabled={!customNeighborhood.trim()}
            size="small"
          >
            Add
          </TouchButton>
        </div>
        
        {/* Selected Neighborhoods */}
        {selectedNeighborhoods.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
              Selected Neighborhoods:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {selectedNeighborhoods.map(neighborhood => (
                <div
                  key={neighborhood}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}
                >
                  <span>{neighborhood}</span>
                  <button
                    onClick={() => toggleNeighborhood(neighborhood)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: '1'
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </TouchFormGroup>

      {/* Quick Selection Presets */}
      <TouchFormGroup>
        <label style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          marginBottom: '12px',
          display: 'block'
        }}>
          ⚡ Quick Presets:
        </label>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <TouchButton
            onClick={() => onTypesChange(['restaurant', 'nightlife'])}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            🍽️ Dining & Nightlife
          </TouchButton>
          
          <TouchButton
            onClick={() => onTypesChange(['hotel', 'attraction', 'museum'])}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            🏨 Tourism
          </TouchButton>
          
          <TouchButton
            onClick={() => onTypesChange(['hospital', 'police', 'pharmacy'])}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            🚨 Emergency Services
          </TouchButton>
          
          <TouchButton
            onClick={() => onTypesChange(['transport', 'bus-station', 'train-station', 'subway', 'airport'])}
            variant="outline"
            size="small"
            style={{ fontSize: '12px' }}
          >
            🚇 Transportation
          </TouchButton>
        </div>
      </TouchFormGroup>

      {/* Active Filters Summary */}
      {(selectedTypes.length > 0 || selectedCategories.length > 0 || selectedNeighborhoods.length > 0) && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fff3e0',
          borderRadius: '8px',
          border: '1px solid #FF9800'
        }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#F57C00', marginBottom: '6px' }}>
            Active Location Filters:
          </div>
          <div style={{ fontSize: '12px', color: '#333' }}>
            {selectedCategories.length > 0 && (
              <div>Categories: {selectedCategories.length} selected</div>
            )}
            {selectedTypes.length > 0 && (
              <div>Types: {selectedTypes.length} selected</div>
            )}
            {selectedNeighborhoods.length > 0 && (
              <div>Neighborhoods: {selectedNeighborhoods.join(', ')}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationTypeFilter;